export const SESSION_MAX_DURATION_MS = 24 * 60 * 60 * 1000;

const SESSION_LOGIN_AT_KEY = "sessionLoginAt";

export const USER_ACCESS_CHANGED_EVENT = "userAccessChanged";

const emitUserAccessChanged = () => {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent(USER_ACCESS_CHANGED_EVENT, {
      detail: {
        hasAdminAccess: getAdminAccess(),
        hasSupervisorAccess: getSupervisorAccess(),
        hasSemanticAdminAccess: getSemanticAdminAccess(),
      },
    })
  );
};

export const setSessionLoginAt = (timestamp = Date.now()) => {
  localStorage.setItem(SESSION_LOGIN_AT_KEY, String(timestamp));
};

export const getSessionLoginAt = () => {
  const raw = localStorage.getItem(SESSION_LOGIN_AT_KEY);
  const parsed = raw ? parseInt(raw, 10) : NaN;
  return Number.isFinite(parsed) ? parsed : null;
};

/** Backfill session start for users already signed in before this feature shipped. */
export const ensureSessionLoginAt = () => {
  if (!getSessionLoginAt()) {
    setSessionLoginAt();
  }
};

export const isSessionExpired = () => {
  const loginAt = getSessionLoginAt();
  if (loginAt === null) return false;
  return Date.now() - loginAt >= SESSION_MAX_DURATION_MS;
};

export const getSessionTimeRemainingMs = () => {
  const loginAt = getSessionLoginAt();
  if (loginAt === null) return SESSION_MAX_DURATION_MS;
  return Math.max(0, SESSION_MAX_DURATION_MS - (Date.now() - loginAt));
};

export const extractUserIdFromEmail = (email) => {
  if (!email || typeof email !== 'string') return 'guest';
  const parts = email.split('@');
  return parts[0];
};

export const storeUserId = (userId) => {
  if (userId) {
    localStorage.setItem('userId', userId);
  }
};

export const getUserId = () => {
  return localStorage.getItem('userId');
};

export const clearUserId = () => {
  localStorage.removeItem('userId');
};

// Admin access helpers
export const setAdminAccess = (hasAccess) => {
  localStorage.setItem('hasAdminAccess', hasAccess ? 'true' : 'false');
  emitUserAccessChanged();
};

export const getAdminAccess = () => {
  return localStorage.getItem('hasAdminAccess') === 'true';
};

// Supervisor access helpers (Sales / Brand / AI Sales Diagnosis dashboards)
export const setSupervisorAccess = (hasAccess) => {
  localStorage.setItem('hasSupervisorAccess', hasAccess ? 'true' : 'false');
  emitUserAccessChanged();
};

export const getSupervisorAccess = () => {
  return localStorage.getItem('hasSupervisorAccess') === 'true';
};

// Semantic admin access helpers (metadata, business-semantic, synonyms)
export const setSemanticAdminAccess = (hasAccess) => {
  localStorage.setItem('hasSemanticAdminAccess', hasAccess ? 'true' : 'false');
  emitUserAccessChanged();
};

export const getSemanticAdminAccess = () => {
  return localStorage.getItem('hasSemanticAdminAccess') === 'true';
};

export const storeUserInfo = (account) => {
  if (account && account.username) {
    const userId = extractUserIdFromEmail(account.username);
    storeUserId(userId);
    
    localStorage.setItem('userEmail', account.username);
    
    if (account.name) {
      localStorage.setItem('userName', account.name);
    }

    const role = 
      account.idTokenClaims?.roles?.[0] || 
      account.idTokenClaims?.role || 
      account.idTokenClaims?.jobTitle ||
      account.idTokenClaims?.extension_Role ||
      'User';
    localStorage.setItem('userRole', role);
  }
};

export const getUserEmail = () => {
  return localStorage.getItem('userEmail') || '';
};

export const getUserName = () => {
  return localStorage.getItem('userName');
};

export const getUserRole = () => {
  return localStorage.getItem('userRole');
};

export const clearUserInfo = () => {
  // Clear user identification and profile
  clearUserId();
  localStorage.removeItem('userEmail');
  localStorage.removeItem('userName');
  localStorage.removeItem('userRole');

  // Clear tokens
  localStorage.removeItem('appToken');
  localStorage.removeItem('appTokenExpiry');
  localStorage.removeItem('databricksToken');
  localStorage.removeItem('databricksTokenExpiry');
  localStorage.removeItem('tokenExpiry');
  localStorage.removeItem(SESSION_LOGIN_AT_KEY);

  // Clear admin access flag
  localStorage.removeItem('hasAdminAccess');
  // Clear supervisor access flag
  localStorage.removeItem('hasSupervisorAccess');
  // Clear semantic admin access flag
  localStorage.removeItem('hasSemanticAdminAccess');

  emitUserAccessChanged();
};
